import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const ContactUsPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Contact Carolina Trophy Roofs | Free Estimates | Rocky Mount, NC</title>
        <meta name="description" content="Contact Carolina Trophy Roofs for free roofing estimates and consultations. Located in Rocky Mount, NC serving Eastern North Carolina. Call (252) 302-2003 or get instant quotes online." />
        <meta name="keywords" content="contact Carolina Trophy Roofs, free roofing estimate, Rocky Mount NC roofer, roofing consultation, roof inspection" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/contact" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Contact Carolina Trophy Roofs | Free Estimates | Rocky Mount, NC" />
        <meta property="og:description" content="Contact us for free roofing estimates and consultations. Located in Rocky Mount, NC serving Eastern North Carolina." />
        <meta property="og:url" content="https://carolinatrophyroofs.com/contact" />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:title" content="Contact Carolina Trophy Roofs | Free Estimates | Rocky Mount, NC" />
        <meta name="twitter:description" content="Contact us for free roofing estimates and consultations. Located in Rocky Mount, NC serving Eastern North Carolina." />
        
        {/* Schema.org Contact Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Carolina Trophy Roofs",
            "description": "Contact Carolina Trophy Roofs for free roofing estimates and consultations in Eastern North Carolina.",
            "mainEntity": {
              "@type": "RoofingContractor",
              "name": "Carolina Trophy Roofs",
              "telephone": "+1-252-302-2003",
              "email": "Claims@CarolinaTrophyRoofs.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "136 S. Business Ct",
                "addressLocality": "Rocky Mount",
                "addressRegion": "NC",
                "postalCode": "27804",
                "addressCountry": "US"
              },
              "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "17:00"
              }
            }
          })}
        </script>
      </Helmet>
      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Carolina Trophy Roofs</h1>
            <p className="text-xl text-gray-600">
              Get in touch with our team for all your roofing needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-2xl font-bold mb-4">Get Your Free INSTANT Estimate</h2>
                <h3 className="text-lg font-medium mb-6">Find Your Property Address Below:</h3>
                
                {/* SkyQuote Embed Container */}
                <div 
                  id="skyquote-contact-form-container"
                  className="w-full bg-white rounded-lg"
                  style={{ minHeight: '600px' }}
                >
                  <iframe 
                    src="https://sky-quote.com/instaquote.html?publicKey=67c78ce800214e84a69b&SkyQuoteUrl=https%3A%2F%2Fsky-quote.com"
                    width="100%" 
                    height="600"
                    style={{border: 'none', borderRadius: '8px', minHeight: '600px'}}
                    allow="geolocation; fullscreen"
                    title="SkyQuote Contact Form"
                    scrolling="auto"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-2xl font-bold mb-4">Schedule a Free Inspection</h2>
                <p className="mb-6">
                  We're happy to provide a free inspection, no-obligation consultation for your roofing project.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => window.open('https://carolinatrophyroofs.sky-quote.com/schedule', '_blank')}
                >
                  Schedule Free Consultation
                </Button>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <Phone className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium">Phone</h3>
                      <p className="mt-1">
                        <a href="tel:+12523022003" className="text-blue-600 hover:underline">
                          (252) 302-2003
                        </a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Mail className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium">Email</h3>
                      <p className="mt-1">
                        <a href="mailto:Claims@CarolinaTrophyRoofs.com" className="text-blue-600 hover:underline">
                          Claims@CarolinaTrophyRoofs.com
                        </a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium">Office Address</h3>
                      <p className="mt-1">
                        136 S. Business Ct<br />
                        Rocky Mount, NC 27804
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Clock className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium">Business Hours</h3>
                      <p className="mt-1">Monday - Friday: 8:00 AM - 5:00 PM</p>
                      <p>Saturday - Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6 text-center">Find Our Rocky Mount Office</h2>
            <div className="w-full bg-gray-200 rounded-lg overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3227.6280592467597!2d-77.77648669999999!3d36.0049536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac335c924526fb%3A0xa2947a49cea69311!2sCAROLINA%20TROPHY%20ROOFS!5e0!3m2!1sen!2sus!4v1750793386585!5m2!1sen!2sus" 
                width="100%" 
                height="400" 
                style={{border: 0}} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Carolina Trophy Roofs Location"
              />
            </div>
          </div>
          
          {/* Address Update Notice */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 italic">
              *We recently moved our Rocky Mount office from 104 to 136 S. Business Ct to accommodate our expanding team and better serve our customers.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactUsPage;
